import { useState, useEffect, useRef, Fragment } from 'react'
import '@google/model-viewer'
import { parseSTL } from '../../utils/parsers/stl.js'
import { parseOBJ } from '../../utils/parsers/obj.js'
import { buildGLB, extractMetadata, applyCanonicalOrientation } from '../../utils/glb-builder.js'
import { getCases, uploadModel } from '../../services/api'

const CONVERT_STATUS = {
  pending:    { label: 'Pending',       pill: 'bg-gray-100 text-gray-600' },
  converting: { label: 'Converting…',  pill: 'bg-blue-100 text-blue-700' },
  done:       { label: 'Ready',        pill: 'bg-green-100 text-green-700' },
  error:      { label: 'Error',        pill: 'bg-red-100 text-red-600' },
}

function ConvertTab() {
  const [queue, setQueue] = useState([])
  const [enableAO, setEnableAO] = useState(true)
  const [converting, setConverting] = useState(false)
  const [preview, setPreview] = useState(null) // { name, blobUrl, metadata }
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  // Upload-to-case state
  const [uploadingItemId, setUploadingItemId] = useState(null) // which item has the case picker open
  const [caseSearch, setCaseSearch] = useState('')
  const [caseResults, setCaseResults] = useState([])
  const [caseLoading, setCaseLoading] = useState(false)
  const caseSearchTimer = useRef(null)

  // Search cases when picker is open
  useEffect(() => {
    if (!uploadingItemId) return
    if (caseSearchTimer.current) clearTimeout(caseSearchTimer.current)
    caseSearchTimer.current = setTimeout(async () => {
      setCaseLoading(true)
      try {
        const res = await getCases({ page: 1, limit: 10, search: caseSearch || undefined })
        setCaseResults(res.data.cases || [])
      } catch { setCaseResults([]) }
      finally { setCaseLoading(false) }
    }, 300)
    return () => { if (caseSearchTimer.current) clearTimeout(caseSearchTimer.current) }
  }, [caseSearch, uploadingItemId])

  async function uploadToCase(item, caseId, caseName) {
    // Upload the ORIGINAL STL/OBJ — backend's stlToGlb.js does the authoritative
    // conversion (Draco + AO). Don't send the client-side preview GLB.
    const ext = item.file.name.split('.').pop().toLowerCase()
    const isStl = ext === 'stl'
    const uploadFile = isStl ? item.file : new File([item.glbBlob], item.file.name.replace(/\.obj$/i, '.glb'), { type: 'model/gltf-binary' })
    setQueue(prev => prev.map(f => f.id === item.id ? { ...f, uploadStatus: 'uploading', uploadProgress: 0 } : f))
    setUploadingItemId(null)
    try {
      await uploadModel(caseId, uploadFile, `Uploaded from Convert tab (${item.file.name})`, (loaded, total) => {
        const pct = total ? Math.round((loaded / total) * 100) : 0
        setQueue(prev => prev.map(f => f.id === item.id ? { ...f, uploadProgress: pct } : f))
      })
      setQueue(prev => prev.map(f => f.id === item.id ? { ...f, uploadStatus: 'uploaded', uploadedCase: caseName } : f))
    } catch (err) {
      console.error('Upload to case failed:', err)
      const msg = err?.response?.data?.error || err?.message || 'Upload failed'
      setQueue(prev => prev.map(f => f.id === item.id ? { ...f, uploadStatus: 'error', uploadError: msg } : f))
    }
  }

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      queue.forEach(item => {
        if (item.blobUrl) URL.revokeObjectURL(item.blobUrl)
      })
    }
  }, [])

  function addFiles(files) {
    const newItems = []
    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase()
      if (ext !== 'stl' && ext !== 'obj') continue
      newItems.push({
        id: crypto.randomUUID(),
        file,
        status: 'pending',
        stage: null, // null | 'parsing' | 'ao' | 'compressing'
        glbBlob: null,
        blobUrl: null,
        metadata: null,
        aoProgress: 0,
        uploadStatus: null, // null | 'uploading' | 'uploaded' | 'error'
        uploadProgress: 0,
        uploadedCase: null,
      })
    }
    if (newItems.length) setQueue(prev => [...prev, ...newItems])
  }

  function removeItem(id) {
    setQueue(prev => {
      const item = prev.find(f => f.id === id)
      if (item?.blobUrl) URL.revokeObjectURL(item.blobUrl)
      return prev.filter(f => f.id !== id)
    })
  }

  function clearAll() {
    queue.forEach(item => { if (item.blobUrl) URL.revokeObjectURL(item.blobUrl) })
    setQueue([])
    closePreview()
  }

  async function convertFile(item) {
    const updateItem = (patch) => setQueue(prev => prev.map(f => f.id === item.id ? { ...f, ...patch } : f))
    const yieldToPaint = () => new Promise(r => setTimeout(r, 32))

    // Fake-progress animator. Uses transform: scaleX() (GPU-composited) so the
    // bar keeps moving even while the main thread is blocked by synchronous
    // parseSTL or Draco WASM. Verified against 132 MB STL in convert-test.html.
    const animateBar = (target, durationSec) => {
      const el = document.querySelector(`[data-convert-bar="${item.id}"]`)
      if (!el) return
      el.style.transition = `transform ${durationSec}s linear`
      void el.offsetWidth
      el.style.transform = `scaleX(${target})`
    }
    const snapBar = (target) => {
      const el = document.querySelector(`[data-convert-bar="${item.id}"]`)
      if (!el) return
      el.style.transition = 'transform 0.3s ease-out'
      void el.offsetWidth
      el.style.transform = `scaleX(${target})`
    }

    try {
      updateItem({ status: 'converting', stage: 'parsing', aoProgress: 0 })
      await yieldToPaint()

      // Stage 1: parsing — estimate ~15 ms / MB for binary STL
      const parseEstimate = Math.max(1.5, item.file.size / 1024 / 1024 * 0.015)
      animateBar(0.4, parseEstimate)

      const arrayBuffer = await item.file.arrayBuffer()
      const ext = item.file.name.split('.').pop().toLowerCase()

      let parsed
      if (ext === 'stl') {
        parsed = parseSTL(arrayBuffer)
      } else {
        const text = new TextDecoder().decode(arrayBuffer)
        parsed = parseOBJ(text)
      }

      const { positions, normals, faceCount } = parsed
      const vertexCount = positions.length / 3
      // Bake the canonical Z-up → Y-up rotation BEFORE metadata/AO so the UI
      // dimensions + AO hemisphere-sampling normals are in the final coord system.
      applyCanonicalOrientation(positions, normals)
      const metadata = extractMetadata(positions, faceCount)

      // AO baking (skip for large meshes)
      let vertexColors
      const doAO = enableAO && vertexCount <= 150000

      if (doAO) {
        updateItem({ stage: 'ao', aoProgress: 0 })
        await yieldToPaint()
        vertexColors = await computeAO(positions, normals, item.id)
      } else {
        vertexColors = new Float32Array(vertexCount * 4)
        for (let i = 0; i < vertexCount; i++) {
          vertexColors[i * 4] = 1.0
          vertexColors[i * 4 + 1] = 1.0
          vertexColors[i * 4 + 2] = 1.0
          vertexColors[i * 4 + 3] = 1.0
        }
      }

      // Stage 2: compressing — estimate ~2.5 µs / vertex for Draco encode
      updateItem({ stage: 'compressing', aoProgress: 100 })
      await yieldToPaint()
      const compressEstimate = Math.max(5, vertexCount * 2.5e-6)
      animateBar(0.95, compressEstimate)
      const glbBytes = await buildGLB(positions, normals, vertexColors)
      const glbBlob = new Blob([glbBytes], { type: 'model/gltf-binary' })
      const blobUrl = URL.createObjectURL(glbBlob)

      snapBar(1.0)
      updateItem({ status: 'done', stage: null, glbBlob, blobUrl, metadata })
    } catch (err) {
      console.error('Conversion error:', err)
      setQueue(prev => prev.map(f => f.id === item.id ? { ...f, status: 'error', stage: null } : f))
    }
  }

  function computeAO(positions, normals, itemId) {
    return new Promise((resolve, reject) => {
      const worker = new Worker(new URL('../../workers/ao-worker.js', import.meta.url), { type: 'module' })

      worker.onmessage = (e) => {
        if (e.data.progress !== undefined) {
          setQueue(prev => prev.map(f => f.id === itemId ? { ...f, aoProgress: e.data.progress } : f))
          // Drive the bar imperatively so it moves in sync with worker progress.
          const el = document.querySelector(`[data-convert-bar="${itemId}"]`)
          if (el) {
            el.style.transition = 'transform 0.2s linear'
            void el.offsetWidth
            el.style.transform = `scaleX(${e.data.progress / 100})`
          }
          return
        }
        if (e.data.colors) {
          resolve(new Float32Array(e.data.colors))
          worker.terminate()
        }
      }
      worker.onerror = (err) => { reject(err); worker.terminate() }

      const posCopy = new Float32Array(positions)
      const normCopy = new Float32Array(normals)
      worker.postMessage({ positions: posCopy, normals: normCopy }, [posCopy.buffer, normCopy.buffer])
    })
  }

  async function convertAll() {
    setConverting(true)
    for (const item of queue) {
      if (item.status !== 'pending') continue
      await convertFile(item)
    }
    setConverting(false)
  }

  function downloadFile(item) {
    if (!item.glbBlob) return
    const url = URL.createObjectURL(item.glbBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = item.file.name.replace(/\.(stl|obj)$/i, '.glb')
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  function showPreview(item) {
    setPreview({
      name: item.file.name.replace(/\.(stl|obj)$/i, '.glb'),
      blobUrl: item.blobUrl,
      metadata: item.metadata,
      glbSize: item.glbBlob?.size || 0,
    })
  }

  function closePreview() {
    setPreview(null)
  }

  function formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const pendingCount = queue.filter(f => f.status === 'pending').length

  return (
    <div className="space-y-6">

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files) }}
        onClick={() => fileInputRef.current?.click()}
        className={`card p-10 text-center cursor-pointer transition-colors border-2 border-dashed ${
          dragOver ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <svg className="w-10 h-10 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>
        <p className="text-sm text-gray-600 font-medium">Drop STL or OBJ files here</p>
        <p className="text-xs text-gray-400 mt-1">or click to browse</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".stl,.obj"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = '' }}
        />
      </div>

      {/* Toolbar */}
      {queue.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={enableAO}
                onChange={(e) => setEnableAO(e.target.checked)}
                className="w-4 h-4 accent-brand-600"
              />
              Ambient Occlusion
              <span className="text-xs text-gray-400">(meshes under 150k verts)</span>
            </label>
          </div>
          <div className="flex gap-2">
            {pendingCount > 0 && (
              <button
                onClick={convertAll}
                disabled={converting}
                className="btn-primary text-sm px-4 py-2"
              >
                {converting ? 'Converting…' : `Convert All (${pendingCount})`}
              </button>
            )}
            <button onClick={clearAll} className="btn-secondary text-sm px-4 py-2">Clear</button>
          </div>
        </div>
      )}

      {/* File queue */}
      {queue.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] text-gray-400 uppercase tracking-wide bg-gray-50">
                <th className="px-4 py-3 text-left font-medium">File</th>
                <th className="px-3 py-3 text-left font-medium">Size</th>
                <th className="px-3 py-3 text-center font-medium">Status</th>
                <th className="px-3 py-3 text-left font-medium">Output</th>
                <th className="px-3 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {queue.map(item => (
                <Fragment key={item.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800 truncate max-w-xs" title={item.file.name}>
                    {item.file.name}
                  </td>
                  <td className="px-3 py-3 text-gray-500">{formatSize(item.file.size)}</td>
                  <td className="px-3 py-3 text-center">
                    {item.status === 'converting' ? (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">
                          {item.stage === 'parsing' ? 'Parsing…'
                            : item.stage === 'ao' ? `Baking AO ${item.aoProgress}%`
                            : item.stage === 'compressing' ? 'Compressing (Draco)…'
                            : 'Converting…'}
                        </span>
                        <div className="w-28 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          {/* transform is driven imperatively from convertFile + AO worker
                              callback so it keeps animating during main-thread blocks (parse,
                              Draco WASM). React must not reset it — keep the inline prop static. */}
                          <div
                            data-convert-bar={item.id}
                            className="h-1.5 rounded-full bg-brand-600"
                            style={{
                              width: '100%',
                              transformOrigin: 'left center',
                              transform: 'scaleX(0)',
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${CONVERT_STATUS[item.status]?.pill || ''}`}>
                        {CONVERT_STATUS[item.status]?.label || item.status}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-500">
                    {item.metadata ? (
                      <span>{item.metadata.vertexCount.toLocaleString()} verts, {item.metadata.faceCount.toLocaleString()} faces — {formatSize(item.glbBlob?.size || 0)}</span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex gap-1.5 justify-end">
                      {item.status === 'pending' && (
                        <button onClick={() => convertFile(item)} className="text-xs text-brand-600 hover:text-brand-800 font-medium">Convert</button>
                      )}
                      {item.status === 'done' && (
                        <>
                          <button onClick={() => showPreview(item)} className="text-xs text-brand-600 hover:text-brand-800 font-medium">Preview</button>
                          <button onClick={() => downloadFile(item)} className="text-xs text-green-600 hover:text-green-800 font-medium">Download</button>
                          {item.uploadStatus === 'uploaded' ? (
                            <span className="text-xs text-green-600">Uploaded to {item.uploadedCase}</span>
                          ) : item.uploadStatus === 'uploading' ? (
                            <span className="text-xs text-blue-600">Uploading {item.uploadProgress}%</span>
                          ) : item.uploadStatus === 'error' ? (
                            <>
                              <span className="text-xs text-red-600" title={item.uploadError || 'Upload failed'}>Failed: {item.uploadError || 'unknown'}</span>
                              <button onClick={() => { setUploadingItemId(item.id); setCaseSearch(''); setCaseResults([]) }} className="text-xs text-purple-600 hover:text-purple-800 font-medium">Retry</button>
                            </>
                          ) : (
                            <button onClick={() => { setUploadingItemId(item.id); setCaseSearch(''); setCaseResults([]) }} className="text-xs text-purple-600 hover:text-purple-800 font-medium">Upload to Case</button>
                          )}
                        </>
                      )}
                      <button onClick={() => removeItem(item.id)} className="text-xs text-gray-400 hover:text-red-500 font-medium">Remove</button>
                    </div>
                  </td>
                </tr>
                {/* Case picker row — shown when "Upload to Case" is clicked */}
                {uploadingItemId === item.id && (
                  <tr className="bg-purple-50">
                    <td colSpan={5} className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-purple-700 font-medium whitespace-nowrap">Select case:</span>
                        <input
                          type="text"
                          value={caseSearch}
                          onChange={(e) => setCaseSearch(e.target.value)}
                          placeholder="Search by case ID or name…"
                          className="input text-sm max-w-[200px]"
                          autoFocus
                        />
                        {caseLoading && <span className="text-xs text-gray-400">Searching…</span>}
                        <div className="flex gap-1.5 flex-wrap flex-1 min-w-0">
                          {caseResults.map(c => (
                            <button
                              key={c.caseId}
                              onClick={() => uploadToCase(item, c.caseId, c.caseId)}
                              className="px-2.5 py-1 bg-white border border-purple-200 rounded-lg text-xs text-purple-700 hover:bg-purple-100 hover:border-purple-400 transition-colors truncate max-w-[220px]"
                              title={c.notes || 'No notes'}
                            >
                              <span className="font-semibold">{c.caseId}</span>{c.notes ? <span className="text-purple-400 ml-1">— {c.notes}</span> : ''}
                            </button>
                          ))}
                          {!caseLoading && caseResults.length === 0 && caseSearch && (
                            <span className="text-xs text-gray-400">No cases found</span>
                          )}
                        </div>
                        <button onClick={() => setUploadingItemId(null)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <p className="text-sm font-semibold text-gray-800">{preview.name}</p>
                {preview.metadata && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {preview.metadata.vertexCount.toLocaleString()} vertices · {preview.metadata.faceCount.toLocaleString()} faces ·{' '}
                    {preview.metadata.dimensions.x.toFixed(1)} × {preview.metadata.dimensions.y.toFixed(1)} × {preview.metadata.dimensions.z.toFixed(1)} mm ·{' '}
                    GLB: {formatSize(preview.glbSize)}
                  </p>
                )}
              </div>
              <button onClick={closePreview} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="h-[500px] bg-gray-100">
              <model-viewer
                src={preview.blobUrl}
                camera-controls=""
                camera-orbit="0deg 90deg auto"
                interaction-prompt="none"
                shadow-intensity="2.5"
                tone-mapping="commerce"
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {queue.length === 0 && (
        <p className="text-center text-sm text-gray-400 py-8">
          Drop STL or OBJ files above to convert them to GLB format. All processing happens in your browser — no files are uploaded.
        </p>
      )}
    </div>
  )
}

export default ConvertTab
